import { Phone, MapPin, Shield } from "lucide-react";

/*
 * LocationHero — Section 1/10 for /garage-door-repair-northern-beaches/
 * MASTER TEMPLATE — duplicate for other locations, change only the LOC block.
 * Paste this ENTIRE file into src/pages/GarageDoorRepairNorthernBeaches/sections/LocationHero.tsx
 */

// ╔══════════════════════════════════════════════════════════════════╗
// ║  LOC CONFIG — change ONLY this block when duplicating to       ║
// ║  other location pages. Everything below adapts automatically.  ║
// ╚══════════════════════════════════════════════════════════════════╝
const LOC = {
  name: "Northern Beaches",
  h1Line1: "Garage Door Repair",
  h1Line2: "on the Northern Beaches",
  subtitle: "Same-day repairs, spring replacement, motor service & new door installs across Manly, Dee Why, Brookvale, Mona Vale and all of the Northern Beaches. Family-run since 2009.",
  tagline: "Manly · Dee Why · Brookvale · Mona Vale · Avalon · Narrabeen",
  count: "15+",
  countLabel: "Northern Beaches",
  heroImg: "/wp-content/uploads/2026/06/loc-northern-beaches-hero.webp",
  watermark: "MANLY",
  chips: ["Manly", "Dee Why", "Brookvale", "Mona Vale", "Avalon", "Narrabeen", "Freshwater", "Collaroy"]
};
// ╚══════════════════════════════════════════════════════════════════╝

const GOLD = "#F5A623";
const NAVY = "#081438";
const NAVY2 = "#0f2347";
const STATS = [{
  value: "8+",
  label: "Brands Serviced"
}, {
  value: "1-Year",
  label: "Labour Warranty"
}, {
  value: LOC.count,
  label: `${LOC.countLabel} Suburbs`
}, {
  value: "5.0 ★",
  label: "Google Rating"
}];
export default function LocationHero() {
  const extraCount = LOC.chips.length < 20 ? 12 : 0;
  return <section id="location-hero" style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* ── Grain ── */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="lh-grain"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#lh-grain)" />
      </svg>

      {/* ── Radial glow ── */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: "-20%",
      left: "-10%",
      width: "60%",
      height: "140%",
      background: `radial-gradient(ellipse,${NAVY2}CC 0%,transparent 70%)`,
      pointerEvents: "none",
      zIndex: 1
    }} />

      {/* ── Top scanline ── */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "2px",
      background: `linear-gradient(90deg,transparent,${GOLD},transparent)`,
      zIndex: 2
    }} />

      {/* ── Watermark ── */}
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: "-2%",
      right: "-2%",
      fontSize: "clamp(80px,14vw,200px)",
      fontWeight: 900,
      color: `${GOLD}0F`,
      lineHeight: 1,
      fontFamily: "Plus Jakarta Sans,sans-serif",
      pointerEvents: "none",
      zIndex: 1,
      userSelect: "none",
      whiteSpace: "nowrap"
    }}>
        {LOC.watermark}
      </div>

      {/* ── Main grid ── */}
      <div style={{
      position: "relative",
      zIndex: 3,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(72px,9vw,112px) clamp(20px,5vw,48px) 0",
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "48px",
      alignItems: "center"
    }} className="lg:!grid-cols-[1fr_1fr]">

        {/* Left — copy */}
        <div>
          {/* Location pill */}
          <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          background: `${GOLD}18`,
          border: `1px solid ${GOLD}35`,
          borderRadius: "999px",
          padding: "6px 16px",
          marginBottom: "24px"
        }}>
            {/** @preserve lucide-do-not-replace */}
            <span data-icon-container="lucide-do-not-replace"><MapPin size={13} color={GOLD} strokeWidth={2} /></span>
            <span style={{
            color: GOLD,
            fontSize: "12px",
            fontWeight: 700,
            fontFamily: "Manrope,sans-serif",
            letterSpacing: "0.06em",
            textTransform: "uppercase"
          }}>
              {LOC.name} · Sydney NSW
            </span>
          </div>

          <h1 style={{
          fontFamily: "Plus Jakarta Sans,sans-serif",
          fontWeight: 800,
          fontSize: "clamp(30px,4.2vw,56px)",
          lineHeight: 1.07,
          color: "#FFFFFF",
          marginBottom: "20px"
        }}>
            {LOC.h1Line1}<br />
            <span style={{
            color: GOLD
          }}>{LOC.h1Line2}</span>
          </h1>

          <p style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "clamp(15px,1.7vw,18px)",
          color: "rgba(255,255,255,0.75)",
          lineHeight: 1.65,
          marginBottom: "14px",
          maxWidth: "520px"
        }}>
            {LOC.subtitle}
          </p>

          {/* Tagline */}
          <p style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "13px",
          color: `${GOLD}BB`,
          fontWeight: 600,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          marginBottom: "12px"
        }}>
            {LOC.tagline}
          </p>

          {/* Warranty line */}
          <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "32px"
        }}>
            {/** @preserve lucide-do-not-replace */}
            <span data-icon-container="lucide-do-not-replace"><Shield size={14} color={GOLD} strokeWidth={2} /></span>
            <span style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "13px",
            color: `${GOLD}BB`,
            fontWeight: 600
          }}>
              1-Year Labour Warranty on every job
            </span>
          </div>

          {/* CTAs */}
          <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "36px"
        }}>
            <a href="tel:0485000933" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: GOLD,
            color: NAVY,
            padding: "13px 26px",
            borderRadius: "8px",
            fontFamily: "Manrope,sans-serif",
            fontWeight: 700,
            fontSize: "15px",
            textDecoration: "none",
            boxShadow: `0 4px 20px ${GOLD}45`,
            whiteSpace: "nowrap"
          }}>
              {/** @preserve lucide-do-not-replace */}
              <span data-icon-container="lucide-do-not-replace"><Phone size={17} color={NAVY} strokeWidth={2.5} /></span>
              0485 000 933
            </a>
            <a href="#contact-us" style={{
            display: "inline-flex",
            alignItems: "center",
            background: "rgba(255,255,255,0.08)",
            color: "#FFF",
            border: "1.5px solid rgba(255,255,255,0.22)",
            padding: "13px 26px",
            borderRadius: "8px",
            fontFamily: "Manrope,sans-serif",
            fontWeight: 600,
            fontSize: "15px",
            textDecoration: "none",
            whiteSpace: "nowrap"
          }}>
              Get Free Quote
            </a>
          </div>

          {/* Suburb chips */}
          <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "7px"
        }}>
            {LOC.chips.map(s => <span key={s} style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "999px",
            padding: "4px 11px",
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            color: "rgba(255,255,255,0.65)",
            fontWeight: 500
          }}>
                {/** @preserve lucide-do-not-replace */}
                <span data-icon-container="lucide-do-not-replace"><MapPin size={9} color={GOLD} strokeWidth={2} /></span>
                {s}
              </span>)}
            {extraCount > 0 && <span style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 11px",
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            color: GOLD,
            fontWeight: 700
          }}>
                +{extraCount} more
              </span>}
          </div>
        </div>

        {/* Right — hero image */}
        <div className="hidden lg:block" style={{
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        aspectRatio: "4/3"
      }}>
          <picture>
            <img src={LOC.heroImg} alt={`Garage door repair ${LOC.name} Sydney`} width={640} height={480} loading="eager" decoding="async" style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block"
          }} src="https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_7240-scaled.webp" />
          </picture>
          <div style={{
          position: "absolute",
          inset: 0,
          border: `1.5px solid ${GOLD}25`,
          borderRadius: "16px",
          pointerEvents: "none"
        }} />
          <div style={{
          position: "absolute",
          bottom: "16px",
          left: "16px",
          background: "rgba(8,20,56,0.85)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${GOLD}40`,
          borderRadius: "8px",
          padding: "8px 14px"
        }}>
            <span style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "12px",
            color: GOLD,
            fontWeight: 700
          }}> Serving {LOC.name} daily</span>
          </div>
        </div>
      </div>

      {/* ── Credibility strip ── */}
      <div style={{
      position: "relative",
      zIndex: 3,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "0 clamp(20px,5vw,48px)"
    }}>
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))",
        borderTop: `1px solid ${GOLD}20`,
        marginTop: "48px",
        paddingTop: "28px",
        paddingBottom: "28px"
      }}>
          {STATS.map(s => <div key={s.label} style={{
          textAlign: "center",
          padding: "8px 12px"
        }}>
              <div style={{
            fontFamily: "Plus Jakarta Sans,sans-serif",
            fontWeight: 800,
            fontSize: "clamp(20px,2.5vw,28px)",
            color: GOLD,
            lineHeight: 1
          }}>{s.value}</div>
              <div style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            color: "rgba(255,255,255,0.5)",
            marginTop: "4px",
            fontWeight: 500
          }}>{s.label}</div>
            </div>)}
        </div>
      </div>

      {/* ── Wave bottom — DUPLICATE in LocationIntro top ── */}
      <div style={{
      position: "relative",
      zIndex: 3,
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill="#FAF7F1" />
        </svg>
      </div>
    </section>;
}